
import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";
import UnitInput from "./UnitInput/UnitInput.vue";
import ColorInput from "./Color/ColorInput.vue";
import DropdownInput from "./DropdownInput.vue";
import { UnitInputValue, UnitInputValueV } from "@/types/unit";
import { isEqual } from "lodash";
import defensiveCopy from "@/util/defensive-copy";
import * as t from "io-ts";
import { pipe } from "fp-ts/lib/function";
import { fold } from "fp-ts/lib/Either";
import { ColorInputV, ColorInputValue } from "@/types/colors";
import Icon from "@/components/UI/Icon.vue";

const BorderSideValueV = t.partial({
  width: UnitInputValueV,
  color: ColorInputV,
  style: t.string
});

type BorderSideValue = t.TypeOf<typeof BorderSideValueV>;

const BorderValueV = t.type({
  top: BorderSideValueV,
  right: BorderSideValueV,
  left: BorderSideValueV,
  bottom: BorderSideValueV
});

type BorderValue = t.TypeOf<typeof BorderValueV>;

type BorderSide = "all" | "top" | "left" | "right" | "bottom";

type BorderProp = "width" | "color" | "style";

const BLANK_BORDER_VALUE = {
  top: {},
  bottom: {},
  left: {},
  right: {}
};

@Component({
  components: { Icon, UnitInput, ColorInput, DropdownInput }
})
export default class BorderComplex extends Vue {
  @Prop() value!: BorderValue | null;

  currentlyEditingSide: BorderSide = "all";

  get internalValue(): BorderValue {
    if (!this.value) {
      return defensiveCopy(BLANK_BORDER_VALUE);
    }

    return pipe(
      this.value,
      BorderValueV.decode,
      fold(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        _errors => {
          /*
          the reason the error logging is commented out is Element Studio:
          if you change a control with an invalid value to a border control,
          the screen feels with errors.
          */

          // NotificationLogger.log.message(
          //   "not a valid BorderValue... probably the types of the copmonents used by the boder complex aer fucked..."
          // );
          // iotsErrorsToCustomErrorType(errors).forEach(
          //   // eslint-disable-next-line @typescript-eslint/no-unused-vars
          //   _e => {}
          //   NotificationLogger.log.message(_e.msg)
          // );
          return defensiveCopy(BLANK_BORDER_VALUE);
        },
        validValue => {
          return validValue;
        }
      )
    );
  }

  get colorOptions() {
    /*
    supporting gradient borders causes a conflict with border-radius
    this is a limitation of browsers / css
    we aren't going to support it unless browser behavior changes
    https://stackoverflow.com/questions/5706963/possible-to-use-border-radius-together-with-a-border-image-which-has-a-gradient
    */
    // if (this.currentlyEditingSide === "all") {
    //   return { type: "solidAndGradient" };
    // }

    return { type: "solidOnly" };
  }

  clear() {
    this.edit({
      top: {},
      bottom: {},
      left: {},
      right: {}
    });
  }

  edit(newValue: BorderValue) {
    this.$emit("input", newValue);
  }

  changeStyle(newStyle: string) {
    const newBorderValue = makeNewBorderValue(
      this.internalValue,
      this.currentlyEditingSide,
      "style",
      newStyle
    );

    this.edit(newBorderValue);
  }

  changeColor(newColor: ColorInputValue) {
    const newBorderValue = makeNewBorderValue(
      this.internalValue,
      this.currentlyEditingSide,
      "color",
      newColor
    );

    this.edit(newBorderValue);
  }

  changeWidth(newWidth: UnitInputValue) {
    const newBorderValue = makeNewBorderValue(
      this.internalValue,
      this.currentlyEditingSide,
      "width",
      newWidth
    );

    this.edit(newBorderValue);
  }

  get width() {
    return this.currentlyEditingSide === "all"
      ? this.valueForAllSidesOrBlank("width")
      : this.internalValue[this.currentlyEditingSide].width;
  }

  get color() {
    return this.currentlyEditingSide === "all"
      ? this.valueForAllSidesOrBlank("color")
      : this.internalValue[this.currentlyEditingSide].color;
  }

  get style() {
    return this.currentlyEditingSide === "all"
      ? this.valueForAllSidesOrBlank("style")
      : this.internalValue[this.currentlyEditingSide].style;
  }

  /* the logic of the 'all' option on this compentent is:
  If the value of each side exactly matches the value of all other sides,
  display the values for any arbitary side */

  valueForAllSidesOrBlank(prop: BorderProp) {
    if (
      isPropSameForAllSides(this.internalValue, prop) &&
      prop in this.internalValue.top
    ) {
      return this.internalValue.top[prop];
    }
    return "";
  }

  get allIconColor() {
    return valuesPresentForSide(this.internalValue, "all") ? "primary" : "";
  }

  get leftIconColor() {
    return valuesPresentForSide(this.internalValue, "left") ? "primary" : "";
  }

  get rightIconColor() {
    return valuesPresentForSide(this.internalValue, "right") ? "primary" : "";
  }

  get bottomIconColor() {
    return valuesPresentForSide(this.internalValue, "bottom") ? "primary" : "";
  }

  get topIconColor() {
    return valuesPresentForSide(this.internalValue, "top") ? "primary" : "";
  }
}

type ValidValueType<T> = T extends "width"
  ? UnitInputValue
  : T extends "color"
  ? ColorInputValue
  : T extends "style"
  ? string
  : never;

function makeNewBorderValue<T extends BorderProp>(
  borderValue: BorderValue,
  side: BorderSide,
  property: T,
  newValue: ValidValueType<T>
) {
  const newBorderValue = defensiveCopy(borderValue);

  if (side === "all") {
    (newBorderValue.top[property] as ValidValueType<T>) = newValue;
    (newBorderValue.bottom[property] as ValidValueType<T>) = newValue;
    (newBorderValue.right[property] as ValidValueType<T>) = newValue;
    (newBorderValue.left[property] as ValidValueType<T>) = newValue;
  } else {
    (newBorderValue[side][property] as ValidValueType<T>) = newValue;
  }

  return newBorderValue;
}

function isPropSameForAllSides(
  borderValue: BorderValue,
  propToCheck: BorderProp
) {
  let lastPropValue = borderValue.top[propToCheck];

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  for (const [key, side] of Object.entries(borderValue)) {
    // need deep equals because width property is of type UnitInputValue (an object)
    if (!isEqual(side[propToCheck], lastPropValue)) return false;
    lastPropValue = side[propToCheck];
  }

  return true;
}

function valuesPresentForSide(borderValue: BorderValue, side: BorderSide) {
  const valuesPresentForAll =
    isPropSameForAllSides(borderValue, "width") &&
    isPropSameForAllSides(borderValue, "style") &&
    isPropSameForAllSides(borderValue, "color");

  if (
    isEqual(borderValue, {
      top: {},
      bottom: {},
      left: {},
      right: {}
    })
  ) {
    return false;
  }

  if (side === "all" && valuesPresentForAll) {
    if (borderValue.top.style || borderValue.top.color) {
      return true;
    }

    return !!borderValue.top.width?.number;
  } else if (side === "all" && !valuesPresentForAll) {
    return false;
  } else if (side !== "all" && valuesPresentForAll) {
    return false;
  } else if (
    side !== "all" &&
    (borderValue[side].style ||
      borderValue[side].color ||
      borderValue[side].width)
  ) {
    return true;
  }

  return false;
}
